import { createSignal, Show, type Component } from 'solid-js';

import { updateSettings } from '../lib/api.js';
import type { PlanStatus, ShopSettings } from '../lib/types.js';

/**
 * The reminders switch (SPEC FR-6).
 *
 * Now an actual `s-switch`. It used to be a button labelled "Turn reminders on" / "Turn reminders
 * off", which is the wrong affordance twice over: a button says "an action happens when you press
 * me" rather than showing state, and its label had to be read as the INVERSE of the current setting
 * — the one place in the app where getting it wrong starts emailing a merchant's customers.
 *
 * Still deliberately off until switched on, and still explicit about who receives the mail.
 */
export const RemindersToggle: Component<{
  settings: ShopSettings;
  plan: PlanStatus;
  onChanged: () => void;
}> = (props) => {
  const [busy, setBusy] = createSignal(false);
  const [message, setMessage] = createSignal<string | null>(null);

  const included = (): boolean => {
    return props.plan.plan !== 'FREE';
  };

  async function toggle(next: boolean): Promise<void> {
    setBusy(true);
    setMessage(null);
    try {
      await updateSettings({ dunningEnabled: next });
      setMessage(
        next
          ? 'Reminders are on. They send overnight, one email per invoice at most.'
          : 'Reminders are off. Nothing will be emailed to your customers.',
      );
      props.onChanged();
    } catch (error) {
      setMessage(`Could not save: ${String(error)} — email support@duebook.app if this persists.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <s-section heading="Payment reminders">
      <s-stack direction="block" gap="base">
        <Show
          when={included()}
          fallback={
            <s-stack direction="block" gap="small">
              <s-text tone="subdued">Off — no email is sent to your customers.</s-text>
              <s-text tone="subdued">
                Automated reminders are included from the Starter plan. Your ledger and credit
                limits keep working on Free — only the sending is paused.
              </s-text>
            </s-stack>
          }
        >
          {/* `prop:checked` rather than an attribute: a boolean attribute is present-or-absent, so
              checked={false} would still render as "on" for anything reading attribute presence. */}
          <s-switch
            label="Send automated payment reminders"
            details={
              props.settings.dunningEnabled
                ? 'Your customers receive reminders as invoices approach and pass their due date.'
                : 'No email is sent to your customers.'
            }
            prop:checked={props.settings.dunningEnabled}
            disabled={busy() ? 'true' : undefined}
            onChange={(event: Event) =>
              void toggle(Boolean((event.target as HTMLInputElement | null)?.checked))
            }
          />
        </Show>
        <Show when={message()}>
          <s-banner tone="info" dismissible>
            <s-paragraph>{message()}</s-paragraph>
          </s-banner>
        </Show>
      </s-stack>
    </s-section>
  );
};
